/**
 * Router assembly: public auth routes, then everything else behind <RequireAuth> + <AppShell>. Registry routes
 * (`src/routes/index.tsx`) are mounted BEFORE every shell-owned route (lottery pages, placeholders) so a feature
 * agent's route with the same path wins: React Router ranks matches by specificity and breaks ties by declaration
 * order, so a shell route declared earlier would shadow a registry twin (E2E-10, D-2201). Shell routes whose path
 * the registry declares are dropped as well, so a conflict can never depend on ranking details.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, createBrowserRouter, type RouteObject } from 'react-router';
import { RequireAuth, RequirePermission } from './auth/guards';
import { useSession } from './auth/session';
import { ErrorBoundary } from './components/ErrorBoundary';
import { isDevBuild } from './lib/runtime-config';
import { ComingSoonPage } from './routes/ComingSoon';
import { ForbiddenPage, NotFoundPage } from './routes/auth/Forbidden';
import { LoginPage } from './routes/auth/Login';
import { PasskeyPage } from './routes/auth/Passkey';
import { TotpPage } from './routes/auth/Totp';
import { VerifyPage } from './routes/auth/Verify';
import { EnrollPage } from './routes/auth/Enroll';
import { DashboardPage } from './routes/dashboard/Dashboard';
import { DevKitPage } from './routes/dev/Kit';
import {
  routes as registryRoutes,
  wizardStepRoutes,
  type ConsoleRoute,
  type ConsoleRouteHandle,
  type WizardStepRoute,
} from './routes/index';
import {
  LotteryAuditTab,
  LotteryDetailPage,
  LotteryDrawTab,
  LotteryOverviewTab,
  LotteryPlaceholderTab,
  LotteryVersionsTab,
} from './routes/lotteries/LotteryDetail';
import { LotteriesListPage } from './routes/lotteries/LotteriesList';
import { ReviewStep } from './routes/lotteries/wizard/ReviewStep';
import { StepStub } from './routes/lotteries/wizard/StepStub';
import { TemplateStep } from './routes/lotteries/wizard/TemplateStep';
import { WizardFrame } from './routes/lotteries/wizard/WizardFrame';
import { AppShell } from './shell/AppShell';
import { homePath } from './shell/navigation';

/** `/` → dashboard when permitted, else the first console the session can see (sponsor viewers → reporting). */
export function Home() {
  const { session, can } = useSession();
  if (can('lottery.read')) return <DashboardPage />;
  const target = homePath(session);
  return target === '/' ? <ForbiddenPage /> : <Navigate to={target} replace />;
}

/** Wraps a registry route in <RequirePermission> when its handle names a permission. */
export const gate = (route: ConsoleRoute): RouteObject => {
  const permission = route.handle?.permission;
  if (!permission || !route.element) return route;
  return {
    ...route,
    element: <RequirePermission permission={permission}>{route.element}</RequirePermission>,
  };
};

const placeholder = (
  path: string,
  title: string,
  capabilities: string,
  owner: string,
  permission?: ConsoleRouteHandle['permission'],
): ConsoleRoute => ({
  path,
  handle: { permission, title },
  element: <ComingSoonPage title={title} capabilities={capabilities} owner={owner} />,
});

/** §16.2 consoles not yet registered by their agents. */
export const PLACEHOLDERS: ConsoleRoute[] = [
  placeholder(
    'draws',
    'Draw control room',
    'Pool reconciliation, seed/snapshot status, inventory checks, two-person controls, reveal rehearsal, static fallback, fairness export.',
    'outcome',
    ['draw.precheck', 'draw.run', 'draw.reveal', 'draw.audit.read'],
  ),
  placeholder(
    'winners',
    'Winners board',
    'Win, claim, verification, platform and messaging state; assignment; win-ID lookup; notes and reminders.',
    'winners',
    'winners.view',
  ),
  placeholder(
    'claims',
    'Claims queue',
    'Age, deadline, missing information, risk flags, external verification result, appeal and dual-review workflow.',
    'claims',
    'claims.view',
  ),
  placeholder(
    'finance',
    'Finance',
    'Balanced ledger, provider reconciliation, refunds, payout maker/checker, fees, evidence exports.',
    'payments',
    ['finance.ledger.read', 'finance.reconcile', 'finance.refund', 'finance.payout.approve'],
  ),
  placeholder(
    'fulfillment',
    'Fulfillment',
    'Owner, SKU, address region, carrier or pickup, dispatch SLA, exception age, delivery evidence.',
    'claims/payments',
    'fulfillment.update',
  ),
  placeholder(
    'audience',
    'Audience',
    'Participant profiles, segments, opted-in exports, engagement task results, follows and joins driven.',
    'analytics',
    'audience.segments.read',
  ),
  placeholder(
    'catalog',
    'Sponsors & prizes',
    'Brands, products, variant matrices, prize stock, sponsors, placements and sampling rules.',
    'prizes/sponsors',
    ['prizes.manage', 'sponsors.manage', 'brands.manage'],
  ),
  placeholder(
    'sponsor-reports',
    'Sponsor reporting',
    'Placement impressions and clicks, sample allocation and claims, aggregate variant preferences, branded export.',
    'sponsors',
    'sponsor.report.view',
  ),
  placeholder(
    'audit',
    'Audit & incidents',
    'Append-only actor/action/before-after evidence, support-case linkage, incident severity, access grants.',
    'analytics/support',
    'audit.read',
  ),
  placeholder(
    'settings',
    'Settings & staff',
    'Staff invitations, roles, MFA resets, tenant policy (high-value threshold), providers and feature flags.',
    'identity',
  ),
];

const stepOverride = (step: WizardStepRoute['step']): ReactNode =>
  wizardStepRoutes.find((s) => s.step === step)?.element ?? <StepStub step={step} />;

const wizardChildren = (): RouteObject[] => [
  { index: true, element: <Navigate to="template" replace /> },
  { path: 'template', element: <TemplateStep /> },
  { path: 'prizes', element: stepOverride('prizes') },
  { path: 'sponsors', element: stepOverride('sponsors') },
  { path: 'engagement', element: stepOverride('engagement') },
  { path: 'rules', element: stepOverride('rules') },
  { path: 'experience', element: stepOverride('experience') },
  { path: 'claims', element: stepOverride('claims') },
  { path: 'review', element: <ReviewStep /> },
];

function ShellWithBoundary() {
  return (
    <AppShell>
      <ErrorBoundary scope="this page">
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  );
}

/** Every path a route tree declares (nested paths joined), used to drop shell routes the registry overrides. */
export const declaredPaths = (routes: readonly RouteObject[], prefix = ''): string[] =>
  routes.flatMap((r) => {
    const own = r.path ? `${prefix}${r.path}` : prefix;
    const nested = r.children ? declaredPaths(r.children, own ? `${own}/` : prefix) : [];
    return r.path ? [own, ...nested] : nested;
  });

export const buildShellRoutes = (
  registry: ConsoleRoute[] = registryRoutes,
  placeholders: ConsoleRoute[] = PLACEHOLDERS,
): RouteObject[] => {
  const registered = new Set(declaredPaths(registry));
  /** Shell-owned pages; each is skipped when the registry declares the same path (registry routes win). */
  const shellOwned: RouteObject[] = [
    { path: 'forbidden', element: <ForbiddenPage /> },
    {
      path: 'lotteries',
      element: <RequirePermission permission="lottery.read" />,
      children: [
        { index: true, element: <LotteriesListPage /> },
        {
          path: 'new',
          element: (
            <RequirePermission permission="lottery.create">
              <ErrorBoundary scope="the wizard">
                <WizardFrame />
              </ErrorBoundary>
            </RequirePermission>
          ),
          children: wizardChildren(),
        },
        {
          path: ':id/edit',
          element: (
            <RequirePermission permission="lottery.update">
              <ErrorBoundary scope="the wizard">
                <WizardFrame />
              </ErrorBoundary>
            </RequirePermission>
          ),
          children: wizardChildren(),
        },
        {
          path: ':id',
          element: <LotteryDetailPage />,
          children: [
            { index: true, element: <LotteryOverviewTab /> },
            { path: 'versions', element: <LotteryVersionsTab /> },
            { path: 'entries', element: <LotteryPlaceholderTab what="Entries" to="/audience" /> },
            { path: 'draw', element: <LotteryDrawTab /> },
            { path: 'winners', element: <LotteryPlaceholderTab what="Winners" to="/winners" /> },
            { path: 'audit', element: <LotteryAuditTab /> },
          ],
        },
      ],
    },
    ...placeholders,
  ];
  return [
    { index: true, element: <Home /> },
    ...registry.map(gate),
    ...shellOwned.filter((r) => !r.path || !registered.has(r.path)).map((r) => gate(r as ConsoleRoute)),
    ...(isDevBuild() ? [{ path: 'dev/kit', element: <DevKitPage /> }] : []),
    { path: '*', element: <NotFoundPage /> },
  ];
};

export const buildAppRoutes = (registry?: ConsoleRoute[]): RouteObject[] => [
  { path: '/login', element: <LoginPage /> },
  { path: '/login/verify', element: <VerifyPage /> },
  { path: '/login/totp', element: <TotpPage /> },
  { path: '/login/enroll', element: <EnrollPage /> },
  { path: '/login/passkey', element: <PasskeyPage /> },
  {
    element: <RequireAuth />,
    children: [{ element: <ShellWithBoundary />, children: buildShellRoutes(registry) }],
  },
];

export const createAppRouter = () => createBrowserRouter(buildAppRoutes());
